"use client"

import React from "react"
import Link from "next/link"
import { useTranslations, useLocale } from "next-intl"
import { usePathname } from "next/navigation"
import { createHashClickHandler } from "../../lib/scroll-to-hash"
import { LanguageToggle } from "./language-toggle"
import { ThemeToggle } from "./theme-toggle"

import { Button } from "../../components/ui/button"
import {
  Sheet,
  SheetTrigger,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "../../components/ui/sheet"

export default function Header() {
  const t = useTranslations("nav")
  const locale = useLocale()
  const pathname = usePathname()

  const handleCaseStudiesClick = React.useMemo(() => {
    return createHashClickHandler(locale, pathname ?? '/', 'case-studies') as unknown as (e: React.MouseEvent) => void
  }, [locale, pathname])

  return (
  <header className="w-full sticky top-0 z-40 backdrop-blur min-h-[var(--header-height)]">
      <div className="mx-auto max-w-5xl px-6 py-3">
        <div className="flex items-center justify-between">
          <Link href={`/${locale}`} className="flex items-center gap-2">
            <img src="/logo-dxag-favicon.png" alt="DX logo" className="h-8 w-8" />
            <span className="text-xl font-bold">
              <span className="hidden sm:block">David Avila</span>
              <span className="block sm:hidden">DXA</span>
            </span>
          </Link>

          {/* Desktop / tablet nav */}
          <nav className="hidden md:flex items-center gap-6">
            <Link className="text-sm font-medium hover:text-primary transition-colors" href={`/${locale}`}>{t("home")}</Link>
            <Link className="text-sm font-medium hover:text-primary transition-colors" href={`/${locale}#services`}>{t("services")}</Link>
            <Link className="text-sm font-medium hover:text-primary transition-colors" href={`/${locale}#case-studies`} onClick={handleCaseStudiesClick}>{t("portfolio")}</Link>
            <Link className="text-sm font-medium hover:text-primary transition-colors" href={`/${locale}#about`}>{t("about")}</Link>
            <Link className="text-sm font-medium hover:text-primary transition-colors" href={`/${locale}/blog`}>{t("blog")}</Link>
            <Link className="text-sm font-medium hover:text-primary transition-colors" href={`/${locale}#contact`}>{t("contact")}</Link>
            <LanguageToggle />
            <ThemeToggle />
          </nav>

          {/* Mobile controls */}
          <div className="flex items-center gap-2 md:hidden">
            <LanguageToggle />
            <ThemeToggle />

            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="Open navigation menu">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-menu h-6 w-6"><line x1="4" x2="20" y1="6" y2="6"></line><line x1="4" x2="20" y1="12" y2="12"></line><line x1="4" x2="20" y1="18" y2="18"></line></svg>
                </Button>
              </SheetTrigger>

                <SheetContent side="right">
                  <SheetHeader>
                    <SheetTitle>Navigation</SheetTitle>
                    <SheetDescription className="sr-only">Primary navigation</SheetDescription>
                  </SheetHeader>

                  <div className="flex flex-col space-y-4 p-4">
                    <Link href={`/${locale}`} className="text-lg font-medium">{t("home")}</Link>
                    <Link href={`/${locale}#services`} className="text-lg font-medium">{t("services")}</Link>
                    <Link href={`/${locale}#case-studies`} className="text-lg font-medium" onClick={handleCaseStudiesClick}>{t("portfolio")}</Link>
                    <Link href={`/${locale}#about`} className="text-lg font-medium">{t("about")}</Link>
                    <Link href={`/${locale}/blog`} className="text-lg font-medium">{t("blog")}</Link>
                    <Link href={`/${locale}#contact`} className="text-lg font-medium">{t("contact")}</Link>
                  </div>
                </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  )
}